"use strict";

/**
 * Single source of truth for integration secret field keys.
 * Imported by Cloud Functions (require) and the SPA (via src/utils/integrationSecrets.js).
 *
 * scope:
 *   - "tenant"    → integrations/{doc} row (+ GSM on tenant project)
 *   - "payableTo" → payableTo/{id}.integrations.{platform}.api
 */

const INTEGRATION_SECRET_SLOTS = {
  "sendgrid:apiKey": {
    scope: "tenant",
    platform: "sendgrid",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "shipstation:apiKey": {
    scope: "tenant",
    platform: "shipstation",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "slack:apiKey": {
    scope: "tenant",
    platform: "slack",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "zoho:password": {
    scope: "tenant",
    platform: "zoho",
    firestorePath: "password",
    tenantGsm: true,
    label: "Client Secret",
  },
  "zoho:apiKey": {
    scope: "tenant",
    platform: "zoho",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "hoodie:accountsApiKey": {
    scope: "tenant",
    platform: "hoodie",
    firestorePath: "index.accounts.apiKey",
    tenantGsm: true,
    label: "Accounts API Key",
  },
  "hoodie:productsApiKey": {
    scope: "tenant",
    platform: "hoodie",
    firestorePath: "index.products.apiKey",
    tenantGsm: true,
    label: "Products API Key",
  },
  "nabis:apiKey": {
    scope: "tenant",
    platform: "nabis",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "method:apiKey": {
    scope: "tenant",
    platform: "method",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "monday:apiKey": {
    scope: "tenant",
    platform: "monday",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "treesnclouds:apiKey": {
    scope: "tenant",
    platform: "treesnclouds",
    firestorePath: "apiKey",
    tenantGsm: true,
    label: "API Key",
  },
  "apextrading:apiKey": {
    scope: "payableTo",
    platform: "apextrading",
    firestorePath: "integrations.apextrading.api",
    tenantGsm: false,
    label: "API Key",
  },
  "leaflink:apiKey": {
    scope: "payableTo",
    platform: "leaflink",
    firestorePath: "integrations.leaflink.api",
    tenantGsm: false,
    label: "API Key",
  },
  "distru:apiKey": {
    scope: "payableTo",
    platform: "distru",
    firestorePath: "integrations.distru.api",
    tenantGsm: false,
    label: "API Key",
  },
  "canix:apiKey": {
    scope: "payableTo",
    platform: "canix",
    firestorePath: "integrations.canix.api",
    tenantGsm: false,
    label: "API Key",
  },
  "flourish:apiKey": {
    scope: "payableTo",
    platform: "flourish",
    firestorePath: "integrations.flourish.api",
    tenantGsm: false,
    label: "API Key",
  },
};

const ALLOWED_FIELD_KEYS = new Set(Object.keys(INTEGRATION_SECRET_SLOTS));

const PAYABLE_TO_SCOPED_FIELD_KEYS = new Set(
  Object.entries(INTEGRATION_SECRET_SLOTS)
    .filter(([, slot]) => slot.scope === "payableTo")
    .map(([fieldKey]) => fieldKey),
);

const PAYABLE_TO_SCOPED_PLATFORMS = [
  ...new Set(
    [...PAYABLE_TO_SCOPED_FIELD_KEYS].map(
      (fieldKey) => INTEGRATION_SECRET_SLOTS[fieldKey].platform,
    ),
  ),
];

const PLATFORMS_WITH_GSM_INTEGRATION_SECRETS = new Set(
  Object.values(INTEGRATION_SECRET_SLOTS)
    .filter((slot) => slot.scope === "tenant" && slot.tenantGsm)
    .map((slot) => slot.platform),
);

function getSlot(fieldKey) {
  return INTEGRATION_SECRET_SLOTS[fieldKey] || null;
}

function platformFromFieldKey(fieldKey) {
  const slot = getSlot(fieldKey);
  if (slot) {
    return slot.platform;
  }
  const idx = fieldKey.indexOf(":");
  return idx > 0 ? fieldKey.slice(0, idx) : "";
}

function isPayableToScopedFieldKey(fieldKey) {
  return PAYABLE_TO_SCOPED_FIELD_KEYS.has(fieldKey);
}

function payableToScopedFieldKey(platform) {
  return `${platform}:apiKey`;
}

function getNestedValue(obj, path) {
  if (!obj || !path) {
    return undefined;
  }
  return path.split(".").reduce((current, key) => current?.[key], obj);
}

function deleteNestedValue(obj, path) {
  if (!obj || !path) {
    return;
  }
  const parts = path.split(".");
  if (parts.length === 1) {
    delete obj[parts[0]];
    return;
  }
  const parent = parts.slice(0, -1).reduce((current, key) => {
    if (current && typeof current === "object") {
      return current[key];
    }
    return undefined;
  }, obj);
  if (parent && typeof parent === "object") {
    delete parent[parts[parts.length - 1]];
  }
}

function getLegacySecretFromDoc(docData, fieldKey) {
  const slot = getSlot(fieldKey);
  if (!slot || !docData || typeof docData !== "object") {
    return null;
  }
  const raw = getNestedValue(docData, slot.firestorePath);
  return typeof raw === "string" ? raw.trim() || null : null;
}

function deleteLegacySecretFromDoc(docLike, fieldKey) {
  const slot = getSlot(fieldKey);
  if (!slot || !docLike || typeof docLike !== "object") {
    return;
  }
  deleteNestedValue(docLike, slot.firestorePath);
}

function getTenantFieldKeysForPlatform(platform) {
  return Object.entries(INTEGRATION_SECRET_SLOTS)
    .filter(([, slot]) => slot.scope === "tenant" && slot.platform === platform)
    .map(([fieldKey]) => fieldKey);
}

function peekLegacySecretOnIntegrationDoc(raw, fieldKey) {
  const legacy = getLegacySecretFromDoc(raw, fieldKey);
  return legacy || "";
}

function deriveLegacyFirestoreSecretSignals(platform, raw) {
  const signals = {};
  if (!platform || !raw) {
    return signals;
  }
  for (const fieldKey of getTenantFieldKeysForPlatform(platform)) {
    const value = peekLegacySecretOnIntegrationDoc(raw, fieldKey);
    if (String(value || "").trim()) {
      signals[fieldKey] = true;
    }
  }
  return signals;
}

function sanitizeIntegrationDocSecretsForViewer(platform, docLike) {
  if (!docLike || typeof docLike !== "object" || !platform) {
    return;
  }
  for (const fieldKey of getTenantFieldKeysForPlatform(platform)) {
    const slot = getSlot(fieldKey);
    if (slot?.tenantGsm) {
      deleteLegacySecretFromDoc(docLike, fieldKey);
    }
  }
}

function sanitizeDraftPlatformSecrets(platform, draft) {
  sanitizeIntegrationDocSecretsForViewer(platform, draft);
}

module.exports = {
  INTEGRATION_SECRET_SLOTS,
  ALLOWED_FIELD_KEYS,
  PAYABLE_TO_SCOPED_FIELD_KEYS,
  PAYABLE_TO_SCOPED_PLATFORMS,
  PLATFORMS_WITH_GSM_INTEGRATION_SECRETS,
  getSlot,
  platformFromFieldKey,
  isPayableToScopedFieldKey,
  payableToScopedFieldKey,
  getNestedValue,
  deleteNestedValue,
  getLegacySecretFromDoc,
  deleteLegacySecretFromDoc,
  getTenantFieldKeysForPlatform,
  peekLegacySecretOnIntegrationDoc,
  deriveLegacyFirestoreSecretSignals,
  sanitizeIntegrationDocSecretsForViewer,
  sanitizeDraftPlatformSecrets,
};
